import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../../app/LanguageContext';
import { mediaFileUrl } from './catalogCompositionUtils';
import {
  CATALOG_OUTPUT_PROFILE_FOLDER_A4_FLAT,
  catalogOutputProfileI18nKey,
} from '../catalogOutputProfiles';

const TemplateCard = ({
  template,
  busyId,
  onEdit,
  onClone,
  onDelete,
}) => {
  const { translate } = useTranslation();
  const imageUrl = mediaFileUrl(template.preview_media_url);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardActionArea onClick={() => onEdit(template.id)} sx={{ flex: 1, alignItems: 'stretch' }}>
        <Box
          sx={{
            position: 'relative',
            height: 220,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : '#ececec',
            overflow: 'hidden',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={template.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" align="center">
                {translate('catalog.templates.card_preview_unavailable')}
              </Typography>
            </Box>
          )}
        </Box>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom noWrap>
            {template.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontFamily="monospace" noWrap>
            {template.slug}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={
                template.output_profile === CATALOG_OUTPUT_PROFILE_FOLDER_A4_FLAT
                  ? translate('catalog.templates.folder_tag')
                  : translate(catalogOutputProfileI18nKey(template.output_profile))
              }
            />
            {template.is_default && (
              <Chip size="small" label={translate('catalog.templates.default')} />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={
                template.is_system
                  ? translate('catalog.templates.type_system')
                  : translate('catalog.templates.type_custom')
              }
            />
          </Box>
          {template.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
              {template.description}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <Tooltip title={translate('catalog.templates.edit')}>
          <IconButton size="small" onClick={() => onEdit(template.id)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={translate('catalog.templates.clone')}>
          <IconButton
            size="small"
            disabled={busyId === template.id}
            onClick={() => onClone(template.id)}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!template.is_system && (
          <Tooltip title={translate('catalog.templates.delete')}>
            <IconButton
              size="small"
              disabled={busyId === template.id}
              onClick={() => onDelete(template.id, template.is_system)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};

export default TemplateCard;
